import { camelCase, isPlainObject, isObject, reduce } from 'lodash-es';

const isArrayOfPrimitives = (arr) => !arr.every((item) => isObject(item));

const processArray = (arr = [], options = {}) => {
  const { deep = false } = options;

  // If we have nested arrays, we iterate over them
  // otherwise we process the object
  return arr.map((child) =>
    deep && Array.isArray(child)
      ? processArray(child, options)
      : // eslint-disable-next-line no-use-before-define
        convertPropsToCamelCase(child, options),
  );
};

/**
 * This method takes in object with snake_case property names
 * and returns a new object with camelCase property names
 *
 * Reasoning for this method is to ensure consistent property
 * naming conventions across JS code.
 *
 * This method also supports additional params in `options` object
 *
 * @param {Object} obj - Object to be converted.
 * @param {Object} options - Object containing additional options.
 * @param {boolean} options.deep - FLag to allow deep object converting
 * @param {Array[]} options.dropKeys - List of properties to discard while building new object
 * @param {Array[]} options.ignoreKeyNames - List of properties to leave intact (as snake_case) while building new object
 */
export const convertPropsToCamelCase = (obj = {}, options = {}) => {
  const { deep = false, dropKeys = [], ignoreKeyNames = [] } = options;

  return Array.isArray(obj)
    ? processArray(obj, options)
    : reduce(
        obj,
        // TODO: https://gitlab.com/gitlab-org/customers-gitlab-com/-/issues/10791
        // Fix this the next time the file is edited.
        // eslint-disable-next-line sonarjs/cognitive-complexity
        (acc, val, prop) => {
          let newValue = val;

          // Drop properties from new object if
          // there are any mentioned in options
          if (dropKeys.indexOf(prop) > -1) {
            return acc;
          }

          if (deep && ((Array.isArray(val) && !isArrayOfPrimitives(val)) || isPlainObject(val))) {
            newValue = Array.isArray(val)
              ? processArray(val, options)
              : convertPropsToCamelCase(val, options);
          }

          // Skip converting properties in new object
          // if there are any mentioned in options
          const newKey = ignoreKeyNames.indexOf(prop) > -1 ? prop : camelCase(prop);
          return { ...acc, [newKey]: newValue };
        },
        {},
      );
};

export default convertPropsToCamelCase;
